import React from "react";
import { cn } from "../cn";

export type StepProgressProps = {
  current: number;
  total: number;
  /** Visible text, e.g. "Step 2 of 5". Also names the bar for assistive tech. */
  label: string;
  /** Name of the step the therapist is on, shown beside the counter. */
  stepName?: string;
  className?: string;
};

/**
 * Progress through a multi-step flow.
 *
 * Replaces two things: the wizard's inline `bg-accent` bar over a grey track,
 * which was a bare div carrying no role and so told a screen reader nothing at
 * all; and `StepIndicator`, a dead component with three hardcoded English
 * labels for a flow that has four or five steps depending on session type.
 *
 * The count is announced as well as drawn, because "how much is left" is the
 * whole point of the control and a coloured bar cannot say it.
 */
const StepProgress: React.FC<StepProgressProps> = ({
  current,
  total,
  label,
  stepName,
  className,
}) => {
  const safeTotal = total > 0 ? total : 1;
  const clamped = Math.min(Math.max(current, 0), safeTotal);

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary">
          {label}
        </span>
        {stepName && (
          <span className="truncate text-sm font-medium text-content-primary">{stepName}</span>
        )}
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={safeTotal}
        aria-valuetext={label}
        className="h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken"
      >
        <span
          className="block h-full rounded-full bg-accent transition-[width] duration-[--duration-slow] ease-[--ease-standard]"
          style={{ width: `${(clamped / safeTotal) * 100}%` }}
        />
      </div>
    </div>
  );
};

export default StepProgress;
